import { type ReactNode } from 'react'
import { cn } from '../ui/utils'

export interface LanguageSwitcherClasses {
  root?: string
  option?: string
  activeOption?: string
  inactiveOption?: string
}

export interface LanguageSwitcherProps<Locale extends string> {
  locales: readonly Locale[]
  locale: Locale
  onLocaleChange: (locale: Locale) => void
  labels: Record<Locale, string>
  ariaLabel: string
  className?: string
  classes?: LanguageSwitcherClasses
  renderLabel?: (locale: Locale, selected: boolean) => ReactNode
}

export function LanguageSwitcher<Locale extends string>({
  locales,
  locale,
  onLocaleChange,
  labels,
  ariaLabel,
  className,
  classes,
  renderLabel,
}: LanguageSwitcherProps<Locale>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'flex items-center gap-0.5 rounded-md border bg-card p-0.5',
        className,
        classes?.root,
      )}
    >
      {locales.map((item) => {
        const selected = item === locale
        return (
          <button
            key={item}
            type="button"
            onClick={() => onLocaleChange(item)}
            aria-label={labels[item]}
            aria-pressed={selected}
            className={cn(
              'rounded px-2 py-0.5 text-xs font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              selected
                ? 'bg-accent text-foreground'
                : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
              classes?.option,
              selected ? classes?.activeOption : classes?.inactiveOption,
            )}
          >
            {renderLabel?.(item, selected) ?? labels[item]}
          </button>
        )
      })}
    </div>
  )
}
